import { useCallback, useEffect, useState } from 'react';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { container } from '@/services/container';
import { useIdentityStore } from '@/store/identity';
import { useUiStore } from '@/store/ui';
import { formatInt } from '@/lib/format';
import { MeshLinkError } from '@/lib/result';
import type { Address, BehaviorRewardEvent, FeedItem } from '@shared/chain';

/** 线上行为单价（×0.001 DPB/分，治理可调）。 */
const UNIT_DPB = 0.001;

/** 行为名 → 友好展示（与 DP 链 30 键奖励表对齐）。 */
const KIND_LABEL: Record<string, string> = {
  post: '发文', image: '发图', video: '发视频', audio: '发语音', live: '发直播',
  repost: '转发', comment: '评论', topic: '话题', review: '晒单',
  follow: '关注', like: '点赞', collect: '收藏', subscribe: '订阅', share: '分享',
  browse: '浏览', browse_complete: '完播', watch_income: '作者分成', watch_income_complete: '完播分成',
  checkin: '签到', checkin_streak: '连续签到', search: '搜索', curated: '精选',
  did: 'DID 注册', stake: '中继质押', referral: '推荐绑定', redeem: '消费核销',
  report: '举报', verify: '验证', verify_identity: '身份验证', verify_device: '设备验证', verify_content: '内容验证',
};

/** 行为分组（展示用）。 */
const GROUPS: Array<{ name: string; keys: string[] }> = [
  { name: '内容创作', keys: ['post', 'image', 'video', 'audio', 'live', 'repost', 'comment', 'topic', 'review'] },
  { name: '社交互动', keys: ['follow', 'like', 'collect', 'subscribe', 'share'] },
  { name: '浏览消费', keys: ['browse', 'browse_complete', 'watch_income', 'watch_income_complete', 'checkin', 'checkin_streak', 'search'] },
  { name: '治理参与', keys: ['did', 'stake', 'referral', 'redeem', 'report', 'verify', 'verify_identity', 'verify_device', 'verify_content', 'curated'] },
];

function dpbOf(points: number): string {
  const v = (Number(points) || 0) * UNIT_DPB;
  return v >= 0.001 ? v.toFixed(3) : v.toFixed(4);
}

/** 全行为挖矿中心：规则展示 + 我的奖励 + 热门内容（生态展示）。 */
export function RewardsPage(): JSX.Element {
  const address = useIdentityStore((s) => s.address);
  const [height, setHeight] = useState(0);
  const [rewards, setRewards] = useState<Record<string, number>>({});
  const [stats, setStats] = useState<Record<string, unknown>>({});
  const [mine, setMine] = useState<BehaviorRewardEvent[]>([]);
  const [feed, setFeed] = useState<FeedItem[]>([]);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async (): Promise<void> => {
    setBusy(true);
    try {
      const [h, st] = await Promise.all([
        container.chain.getBlockHeight().catch(() => 0),
        container.chain.meshStats().catch(() => ({}) as Record<string, unknown>),
      ]);
      setHeight(h);
      setStats(st ?? {});
      const params = (st?.params ?? {}) as Record<string, unknown>;
      setRewards((params.behavior_rewards as Record<string, number>) ?? {});
      if (address) setMine(await container.chain.myRewards(address as Address, 50).catch(() => []));
      setFeed(await container.chain.recommendedFeed(10).catch(() => []));
    } catch (err) {
      useUiStore.getState().pushError(err instanceof MeshLinkError ? err.code : 'E_UNKNOWN');
    } finally {
      setBusy(false);
    }
  }, [address]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const mineTotal = mine.reduce((s, e) => s + Number(e.data?.amount ?? 0), 0);
  const allKeys = new Set([...Object.keys(rewards), ...GROUPS.flatMap((g) => g.keys)]);

  return (
    <div className="space-y-4">
      {/* 链状态 + 我的奖励摘要 */}
      <section className="card p-5">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-sm font-medium text-text">全行为挖矿</h2>
          <Badge tone="ok">链高度 {formatInt(height)}</Badge>
        </div>
        <p className="mt-1 text-xs leading-relaxed text-dim">
          不按时间出 DPB，按定义行为出 DPB：任何链上行为完成即自动增发奖励，无需领取。
          防刷护栏：信誉系数 ×0.05 · 单账号日上限 · 全局日发行上限。
        </p>
        <div className="mt-3 grid grid-cols-2 gap-2 text-center">
          <div className="rounded-btn bg-panel-2 px-3 py-2">
            <div className="text-lg font-semibold text-text">{mine.length}</div>
            <div className="text-[11px] text-dim">我的奖励笔数</div>
          </div>
          <div className="rounded-btn bg-panel-2 px-3 py-2">
            <div className="text-lg font-semibold text-text">{mineTotal.toFixed(4)}</div>
            <div className="text-[11px] text-dim">累计奖励 DPB</div>
          </div>
        </div>
        <div className="mt-3 flex gap-2">
          <Button variant="outline" size="sm" loading={busy} onClick={() => void refresh()}>
            刷新
          </Button>
        </div>
      </section>

      {/* 行为奖励规则 */}
      <section className="card p-5">
        <h2 className="mb-3 text-sm font-medium text-text">行为奖励规则（×{UNIT_DPB} DPB/分）</h2>
        {allKeys.size === 0 ? (
          <EmptyState title="奖励表未加载" description="连接链后显示全部行为规则" />
        ) : (
          <div className="space-y-3">
            {GROUPS.map((g) => {
              const rows = g.keys.filter((k) => allKeys.has(k));
              if (rows.length === 0) return null;
              return (
                <div key={g.name}>
                  <div className="mb-1 text-xs font-medium text-accent">{g.name}</div>
                  <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-3">
                    {rows.map((k) => {
                      const p = rewards[k] ?? 0;
                      return (
                        <div key={k} className="flex items-center justify-between rounded-btn bg-panel-2 px-2.5 py-1.5 text-xs">
                          <span className="text-text">{KIND_LABEL[k] ?? k}</span>
                          <span className="text-dim">{p ? `${p} 分 ≈ ${dpbOf(p)} DPB` : '待治理'}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
            {Object.keys(rewards).filter((k) => !GROUPS.flatMap((g) => g.keys).includes(k)).length > 0 ? (
              <div>
                <div className="mb-1 text-xs font-medium text-accent">其它</div>
                <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-3">
                  {Object.keys(rewards)
                    .filter((k) => !GROUPS.flatMap((g) => g.keys).includes(k))
                    .map((k) => (
                      <div key={k} className="flex items-center justify-between rounded-btn bg-panel-2 px-2.5 py-1.5 text-xs">
                        <span className="text-text">{KIND_LABEL[k] ?? k}</span>
                        <span className="text-dim">{dpbOf(rewards[k])} DPB</span>
                      </div>
                    ))}
                </div>
              </div>
            ) : null}
          </div>
        )}
        <p className="mt-3 text-[11px] text-dim">金额按线上单价折算展示，治理可经 set_params 调档；统计面板另有 {String(stats.anchors ?? 0)} 条内容 · {String(stats.dids ?? 0)} 个 DID · {String(stats.promos ?? 0)} 个促销。</p>
      </section>

      {/* 我的奖励记录 */}
      <section className="card p-5">
        <h2 className="mb-3 text-sm font-medium text-text">我的奖励记录</h2>
        {mine.length === 0 ? (
          <EmptyState title="还没有奖励记录" description="去发内容、点赞、浏览、签到——行为上链自动获得 DPB" />
        ) : (
          <ul className="space-y-2">
            {mine.slice(0, 20).map((e, i) => (
              <li key={`${e.height}-${i}`} className="flex items-center justify-between rounded-btn bg-panel-2 px-3 py-2 text-xs">
                <span className="text-text">{KIND_LABEL[String(e.data?.kind ?? '')] ?? e.data?.kind ?? '行为奖励'}</span>
                <span className="text-dim">信誉 ×{Number(e.data?.coef ?? 1)}</span>
                <span className="font-medium text-text">{Number(e.data?.amount ?? 0).toFixed(4)} DPB</span>
                <span className="mono text-dim">#{e.height}</span>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* 热门内容（推荐流展示） */}
      <section className="card p-5">
        <h2 className="mb-3 text-sm font-medium text-text">热门内容 · 推荐流</h2>
        {feed.length === 0 ? (
          <EmptyState title="暂无推荐" description="按浏览/完播/点赞/收藏/分享信号排序，越新越热" />
        ) : (
          <ul className="space-y-2">
            {feed.map((f) => (
              <li key={f.cid} className="flex items-center justify-between gap-2 rounded-btn bg-panel-2 px-3 py-2 text-xs">
                <span className="mono text-dim">{String(f.cid).slice(0, 14)}…</span>
                <span className="text-text">{KIND_LABEL[String(f.kind ?? '')] ?? f.kind ?? '内容'}</span>
                <span className="text-dim">{String(f.author ?? '').slice(0, 10)}…</span>
                <span className="text-accent">热度 {Number(f.score ?? 0).toFixed(1)}</span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

export default RewardsPage;
